// @flow
import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'

import Tooltip from 'app/common/ui/Tooltip'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'
import { DotsLoader } from 'app/common/components/Loader'
import { useCurrencySign } from 'app/common/components-next/currency'
import PlainIcon, { iconTypes, colorTypes } from 'app/common/ui-next/plain-icon'

import ComplexDishItem from '../../commonComponents/ComplexItem/ComplexDishItem'
import type { ComplexApiType } from '../../MenuComplexesApi'
import type { OrderDataItemType } from '../../CafeteriaMenu/OrderData'
import useComplexesFree from '../../CafeteriaMenu/useComplexesFree'

import styles from './Complex.module.scss'

const cx = classNames.bind(styles)

type Props = {
  orderFormOpened: boolean,
  balanceLeft?: number,
  addToOrder: (item: OrderDataItemType) => void,
  isAdded: boolean,
  loading: boolean,
  personId: number,
  date: string,
  ...$Exact<ComplexApiType>,
}

const Complex = ({
  orderFormOpened,
  balanceLeft = 0,
  addToOrder,
  isAdded,
  loading,
  available,
  cost,
  title,
  items,
  guid,
  personId,
  date,
  free_for_sale: freeFromOrder,
}: Props) => {
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign()

  const params = useMemo(
    () => ({
      date,
      customer_id: personId,
      complex_guid: guid,
      // purchase_guid:
    }),
    [date, personId, guid],
  )

  const [currentlyFree, gratLoading] = useComplexesFree(params) // бесплатный комплект

  const free = freeFromOrder || currentlyFree

  const notEnough = orderFormOpened && balanceLeft - cost < 0
  const notEnabled = orderFormOpened && !available
  const locked = notEnabled || (notEnough && !free) || (notEnough && isAdded && orderFormOpened)

  const reason = (() => {
    if (notEnabled) {
      return t('order_form_kit_denied', 'Недоступно для заказа')
    }
    if (
      (orderFormOpened && !isAdded && notEnough && !free) ||
      (orderFormOpened && isAdded && notEnough)
    ) {
      return t('order_form_kit_insufficient', 'Недостаточно средств')
    }
    return ''
  })()

  const addedToOrder = orderFormOpened && isAdded
  const notAvailable = locked || loading || gratLoading

  // добавление в заказ
  const handleAddToOrder = useCallback(
    (e) => {
      e.preventDefault()
      // если уже добавлено, форма не открыта или не доступно добавление для этого товара,
      // то просто выходим
      if (addedToOrder || !orderFormOpened || notAvailable) return

      addToOrder({
        guid,
        n: 1,
        cost,
        type: 1,
        title,
        free_for_sale: free,
      })
    },
    [notAvailable, addedToOrder, orderFormOpened, guid, cost, title, addToOrder, free],
  )

  const wait = loading || gratLoading

  return (
    <div // eslint-disable-line jsx-a11y/click-events-have-key-events
      className={cx(styles.root, { notAvailable, addedToOrder })}
      onClick={handleAddToOrder}
    >
      <div className={styles.complexSidebar}>
        <PlainIcon
          width="38px"
          height="auto"
          type={iconTypes.COMPLEX}
          colorType={colorTypes.COLOR_PRIMARY}
        />
      </div>
      <div className={styles.complexMain}>
        <div className={styles.complexTitle}>
          {!reason && !notAvailable ? <Tooltip text={title}>{title}</Tooltip> : title}
        </div>
        <div>
          {items.map((item, i) => {
            const { title: tt } = item

            return (
              <ComplexDishItem
                key={`${i}_${tt}`}
                index={i}
                isTip={!reason && !notAvailable}
                // $FlowFixMe
                {...item}
              />
            )
          })}
        </div>
        <div className={cx(styles.complexCost, { notAvailable })}>
          {toFixedValueCurrency(cost, false, currency)}
        </div>
      </div>
      {wait && <DotsLoader className={styles.dotsLoader} />}
      <Tooltip text={reason} className={styles.tooltip} />
    </div>
  )
}

export default Complex
